import { OrganizationRecord } from 'hooks/use-organization-list/use-organization-list.types';
import { TrialResponse } from 'sync/trial-service/trial-service.types';
import { User } from 'sync/user-service';
import { Pendo } from 'types/pendo';
import { debugLog } from 'utils/debug';

const withPendo = (callback: (pendo: Partial<Pendo>) => void) => {
  if (window && window.pendo) {
    callback(window.pendo);
  }
};

export const pendoLogout = () =>
  withPendo((pendo) => {
    debugLog('/utils/pendo logout');

    pendo.clearSession?.();
  });

const getAccountType = (trial?: Partial<TrialResponse>): 'paid' | 'trial' | 'trial_sales_assisted' | 'trial_free_tier' => {
  if (!trial || Object.keys(trial).length === 0 || trial?.data?.activation?.status === 'paid') {
    return 'paid';
  }

  if (trial.data?.activation?.isSalesAssisted) {
    return 'trial_sales_assisted';
  }

  if (trial?.data?.isFreeTier) {
    return 'trial_free_tier';
  }

  return 'trial';
};

const getAccountMetadata = (organizationRecord: OrganizationRecord) => {
  const accountType = getAccountType(organizationRecord?.trial);
  return {
    id: organizationRecord?.organizationId,
    accountId: organizationRecord?.organizationId,
    accountName: organizationRecord?.organization.data?.name,
    accountType,
    accountTrialDaysLeft: (accountType === 'trial' && organizationRecord?.trial?.data?.activation?.daysLeftForTrial) || undefined,
  };
};

const updateOrganization = (pendo: Partial<Pendo>, organizationRecord: OrganizationRecord) => {
  debugLog('/utils/pendo updateOrganization');

  if (!pendo.getSerializedMetadata || !pendo.updateOptions) {
    return;
  }

  const { account, visitor } = pendo.getSerializedMetadata();

  pendo.updateOptions({
    visitor,
    account: {
      ...account,
      ...getAccountMetadata(organizationRecord),
    },
  });
};

const initializePendo = (pendo: Partial<Pendo>, user: User | undefined, organizationRecord: OrganizationRecord) => {
  debugLog('/utils/pendo login');

  // If we didn't get here from login page (if user SESSION is already
  // logged in), then we have to initialize(), not identify().

  const identifyingFn = pendo.isReady?.() ? pendo.identify : pendo.initialize;

  if (identifyingFn) {
    identifyingFn({
      visitor: {
        id: user?.email,
        email: user?.email,
        createdAt: user?.audit.createdAt,
      },
      account: getAccountMetadata(organizationRecord),
    });
  }
};

export const loginPendo = (organizationRecord: OrganizationRecord, user?: User) => {
  withPendo((pendo) => initializePendo(pendo, user, organizationRecord));
};

export const onChangePendoOrganization = (organizationRecord: OrganizationRecord) => {
  withPendo((pendo) => updateOrganization(pendo, organizationRecord));
};
